// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third-Party Library Imports
import { useDispatch, useSelector } from "react-redux";

// Local Component Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import NoAccess from "./components/no-access";
import ResultStep from "./components/result-step";
import TemplateStep from "./components/template-step";
import UploadStep from "./components/upload-step";

// Service Imports
import { CREATE_BREADCRUMB } from "../../store/actions";
import PortfolioAgreementsBulkService from "./portfolio-agreements-bulk.service";
import { BulkMode, BulkResult, INITIATE_PERMISSION } from "./types";

import "./portfolio-agreements-bulk.scss";

type Busy = "template" | BulkMode | null;

/**
 * Bulk initiation of portfolio agreement amendments and appendices.
 *
 * Two passes over one endpoint: `validate` is read-only and can be run as
 * often as needed, `execute` revalidates transactionally and inserts the rows
 * that are still valid as Pending records. Nothing about the workbook is
 * checked in the browser — validate mode is the authority on what is valid.
 */
const PortfolioAgreementsBulk: React.FC = (): ReactElement => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const canInitiate = user?.permissions?.includes(INITIATE_PERMISSION);
  const service = new PortfolioAgreementsBulkService(dispatch);

  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkResult | null>(null);
  const [busy, setBusy] = useState<Busy>(null);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          { text: "Portfolios", type: "link", link: "/portfolio" },
          {
            text: "Portfolio agreements",
            type: "link",
            link: "/portfolio?tab=4",
          },
        ],
      },
    });
  }, []);

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const run = async (mode: BulkMode) => {
    if (!file) return;
    setBusy(mode);
    const next = await service.upload(file, mode);
    setBusy(null);
    if (next) setResult(next);
  };

  const downloadTemplate = () => {
    setBusy("template");
    void service.downloadTemplate().finally(() => setBusy(null));
  };

  return (
    <section className="pa-bulk-container">
      <BreadcrumbComponent />

      <div className="page-title">
        <h3>Bulk initiation — amendments and appendices</h3>
        <p>
          Download the template, fill in one row per portfolio, then validate
          the file before executing. A row of type <strong>BOTH</strong> is
          expanded into a separate amendment record and appendix record.
        </p>
      </div>

      {!canInitiate ? (
        <NoAccess />
      ) : (
        <>
          <TemplateStep
            busy={busy === "template"}
            disabled={busy !== null}
            onDownload={downloadTemplate}
          />

          <UploadStep
            file={file}
            busy={busy !== null}
            validating={busy === "validate"}
            executed={result?.mode === "execute"}
            onFile={(selected) => {
              setResult(null);
              setFile(selected);
            }}
            onReset={reset}
            onValidate={() => void run("validate")}
          />

          {result && (
            <ResultStep
              result={result}
              busy={busy !== null}
              executing={busy === "execute"}
                  onExecute={() => void run("execute")}
            />
          )}
        </>
      )}
    </section>
  );
};

export default PortfolioAgreementsBulk;
